import * as CONSTANTS from "../utils/constants/constants";
import axios from "axios";

class ClinicService {
    // ================================
    // CLINIC Management
    // ================================
    
    /**
     * Get all clinics
     * @param search - Optional search filter by clinic name
     * @param includeCohorts - Whether to include cohorts in response
     */
    static getAllClinics(search?: string, includeCohorts?: boolean) {
        let url = CONSTANTS.HOST + "api/v1/cohort/clinics/";
        const params = new URLSearchParams();
        
        if (search) {
            params.append('search', search);
        }
        if (includeCohorts) {
            params.append('cohorts', 'true');
        }
        
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
        
        return url;
    }

    /**
     * Get clinic details by reference
     * @param clinic_reference - The clinic reference/identifier
     * @param includeCohorts - Whether to include associated cohorts
     */
    static getClinic(clinic_reference: string, includeCohorts?: boolean) {
        let url = CONSTANTS.HOST + "api/v1/cohort/clinics/" + clinic_reference + "/";
        if (includeCohorts) {
            url += "?cohorts=true";
        }
        return url;
    }

    /**
     * Create a new clinic
     */
    static createClinic(data: any, access_token: string) {
        return axios
            .post(CONSTANTS.HOST + "api/v1/cohort/clinics/", data, {
                headers: { 
                    authorization: "Bearer " + access_token,
                    "Content-Type": "application/json"
                },
            })
            .then((response: any) => response)
            .catch((err: any) => {
                console.log(err);
                return err.response;
            });
    }

    /**
     * Patch clinic (partial update)  
     */
    static patchClinic(clinic_reference: string, data: any, access_token: string) {
        return axios
            .patch(
                CONSTANTS.HOST + "api/v1/cohort/clinics/" + clinic_reference + "/",
                data,
                {
                    headers: { 
                        authorization: "Bearer " + access_token,
                        "Content-Type": "application/json"
                    },
                }
            )
            .then((response: any) => response)
            .catch((err: any) => {
                console.log(err);
                return err.response;
            });
    }

    /**
     * Delete clinic (soft delete)
     */
    static deleteClinic(clinic_reference: string, access_token: string) {
        return axios
            .delete(CONSTANTS.HOST + "api/v1/cohort/clinics/" + clinic_reference + "/", {
                headers: { authorization: "Bearer " + access_token },
            })
            .then((response: any) => response)
            .catch((err: any) => {
                console.log(err);
                return err.response;
            });
    }

    /**
     * Get clinic's cohorts
     */
    static getClinicCohorts(clinic_reference: string, includePatients?: boolean) {
        let url = CONSTANTS.HOST + "api/v1/cohort/clinics/" + clinic_reference + "/cohorts/";
        if (includePatients) {
            url += "?patients=true";
        }
        return url;
    }

    // ================================
    // COHORT Management
    // ================================

    /**
     * Get all cohorts
     * @param clinic_id - Optional filter by clinic ID
     * @param search - Optional search filter by cohort name
     * @param includePatients - Whether to include patient associations
     */
    static getAllCohorts(clinic_id?: string, search?: string, includePatients?: boolean) {
        let url = CONSTANTS.HOST + "api/v1/cohort/cohorts/";
        const params = new URLSearchParams();
        
        if (clinic_id) {
            params.append('clinic_id', clinic_id);
        }
        if (search) {
            params.append('search', search);
        }
        if (includePatients) {
            params.append('patients', 'true');
        }
        
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
        
        return url;
    }

    /**
     * Get cohort details by reference
     */
    static getCohort(cohort_reference: string, includeInactive?: boolean) {
        let url = CONSTANTS.HOST + "api/v1/cohort/cohorts/" + cohort_reference + "/";
        if (includeInactive) {
            url += "?include_inactive=true";
        }
        return url;
    }

    /**
     * Create a new cohort
     */
    static createCohort(data: any, access_token: string) {
        console.log("ClinicService.createCohort - Sending data:", JSON.stringify(data));
        console.log("ClinicService.createCohort - Data object keys:", Object.keys(data));
        console.log("ClinicService.createCohort - clinic_id value:", data.clinic_id, typeof data.clinic_id);
        
        return axios
            .post(CONSTANTS.HOST + "api/v1/cohort/cohorts/", data, {
                headers: { 
                    authorization: "Bearer " + access_token,
                    "Content-Type": "application/json"
                },
            })
            .then((response: any) => response)
            .catch((err: any) => {
                console.log("ClinicService.createCohort error:", err);
                return err.response;
            });
    }

    /**
     * Update cohort (full update)
     */
    static updateCohort(cohort_reference: string, data: any, access_token: string) {
        return axios
            .put(
                CONSTANTS.HOST + "api/v1/cohort/cohorts/" + cohort_reference + "/",
                data,
                {
                    headers: { 
                        authorization: "Bearer " + access_token,
                        "Content-Type": "application/json"
                    },
                }
            )
            .then((response: any) => response)
            .catch((err: any) => {
                console.log(err);
                return err.response;
            });
    }

    /**
     * Partially update cohort
     */
    static patchCohort(cohort_reference: string, data: any, access_token: string) {
        return axios
            .patch(
                CONSTANTS.HOST + "api/v1/cohort/cohorts/" + cohort_reference + "/",
                data,
                {
                    headers: { 
                        authorization: "Bearer " + access_token,
                        "Content-Type": "application/json"
                    },
                }
            )
            .then((response: any) => response)
            .catch((err: any) => {
                console.log(err);
                return err.response;
            });
    }

    /**
     * Delete cohort (soft delete)
     */
    static deleteCohort(cohort_reference: string, access_token: string) {
        return axios
            .delete(CONSTANTS.HOST + "api/v1/cohort/cohorts/" + cohort_reference + "/", {
                headers: { authorization: "Bearer " + access_token },
            })
            .then((response: any) => response)
            .catch((err: any) => {
                console.log(err);
                return err.response;
            });
    }

    // ================================
    // PATIENT ASSOCIATION Management
    // ================================

    /**
     * Get patient associations
     */
    static getPatientAssociations(cohort_id?: string, patient_id?: string, is_active?: boolean) {
        let url = CONSTANTS.HOST + "api/v1/cohort/patient-associations/";
        const params = new URLSearchParams();
        
        if (cohort_id) {
            params.append('cohort_id', cohort_id);
        }
        if (patient_id) {
            params.append('patient_id', patient_id);
        }
        if (is_active !== undefined) {
            params.append('is_active', is_active.toString());
        }
        
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
        
        return url;
    }

    /**
     * Associate single patient to cohort
     */
    static associatePatient(data: any, access_token: string) {
        return axios
            .post(CONSTANTS.HOST + "api/v1/cohort/patient-associations/", data, {
                headers: { 
                    authorization: "Bearer " + access_token,
                    "Content-Type": "application/json"
                },
            })
            .then((response: any) => response)
            .catch((err: any) => {
                console.log(err);
                return err.response;
            });
    }

    /**
     * Associate multiple patients to cohort (bulk operation)
     */
    static bulkAssociatePatients(data: any, access_token: string) {
        return axios
            .post(CONSTANTS.HOST + "api/v1/cohort/patient-associations/bulk/", data, {
                headers: { 
                    authorization: "Bearer " + access_token,
                    "Content-Type": "application/json"
                },
            })
            .then((response: any) => response)
            .catch((err: any) => {
                console.log(err);
                return err.response;
            });
    }

    /**
     * Remove single patient from cohort
     */
    static removePatientFromCohort(data: any, access_token: string) {
        return axios
            .delete(CONSTANTS.HOST + "api/v1/cohort/patient-associations/remove/", {
                headers: { 
                    authorization: "Bearer " + access_token,
                    "Content-Type": "application/json"
                },
                data: data
            })
            .then((response: any) => response)
            .catch((err: any) => {
                console.log(err);
                return err.response;
            });
    }

    /**
     * Remove multiple patients from cohort (bulk operation)
     */
    static bulkRemovePatients(data: any, access_token: string) {
        const url = CONSTANTS.HOST + "api/v1/cohort/patient-associations/bulk-remove/";
        // Defensive: ensure payload is plain object and we send proper JSON.
        const payload = {
            cohort_id: data?.cohort_id,
            patient_ids: Array.isArray(data?.patient_ids) ? data.patient_ids : [],
        };
        console.debug("bulkRemovePatients - sending DELETE", {
            url,
            payloadType: typeof payload,
            payload,
        });
        return axios.delete(url, {
            headers: {
                authorization: "Bearer " + access_token,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            // Let axios handle JSON serialization automatically for DRF
            data: payload
        }).then((response: any) => response).catch((err: any) => {
            console.error('bulkRemovePatients DELETE failed', {
                status: err.response?.status,
                statusText: err.response?.statusText,
                data: err.response?.data,
            });
            return err.response;
        });
    }

    /**
     * Update patient association
     */
    static updatePatientAssociation(association_reference: string, data: any, access_token: string) {
        return axios
            .put(
                CONSTANTS.HOST + "api/v1/cohort/patient-associations/" + association_reference + "/",
                data,
                {
                    headers: { 
                        authorization: "Bearer " + access_token,
                        "Content-Type": "application/json"
                    },
                }
            )
            .then((response: any) => response)
            .catch((err: any) => {
                console.log(err);
                return err.response;
            });
    }

    /**
     * Remove patient by association reference
     */
    static removePatientAssociation(association_reference: string, access_token: string) {
        return axios
            .delete(CONSTANTS.HOST + "api/v1/cohort/patient-associations/" + association_reference + "/", {
                headers: { authorization: "Bearer " + access_token },
            })
            .then((response: any) => response)
            .catch((err: any) => {
                console.log(err);
                return err.response;
            });
    }

    // ================================
    // ACCESS CONTROL Management
    // ================================

    /**
     * Grant access to clinic
     */
    static grantClinicAccess(clinic_reference: string, data: any, access_token: string) {
        return axios
            .post(
                CONSTANTS.HOST + "api/v1/cohort/clinics/" + clinic_reference + "/grant-access/",
                data,
                {
                    headers: { 
                        authorization: "Bearer " + access_token,
                        "Content-Type": "application/json"
                    },
                }
            )
            .then((response: any) => response)
            .catch((err: any) => {
                console.log(err);
                return err.response;
            });
    }

    /**
     * List clinic access records
     */
    static getClinicAccess(clinic_reference: string) {
        return CONSTANTS.HOST + "api/v1/cohort/clinics/" + clinic_reference + "/access/";
    }

    /**
     * Manage cohort access
     */
    static manageCohortAccess(data: any, access_token: string) {
        return axios
            .post(CONSTANTS.HOST + "api/v1/cohort/cohort-access/", data, {
                headers: { 
                    authorization: "Bearer " + access_token,
                    "Content-Type": "application/json"
                },
            })
            .then((response: any) => response)
            .catch((err: any) => {
                console.log(err);
                return err.response;
            });
    }

    // ================================
    // Legacy compatibility methods (for gradual migration)
    // ================================

    /**
     * Legacy method - maps to getAllClinics with cohorts
     * @deprecated Use getAllClinics instead
     */
    static getAllGroups() {
        return ClinicService.getAllClinics(undefined, true);
    }

    /**
     * Legacy method - maps to getClinic
     * @deprecated Use getClinic instead
     */
    static getOneGroup(clinic_reference: string) {
        return ClinicService.getClinic(clinic_reference, true);
    }

    /**
     * Legacy method - maps to getClinicCohorts for device compatibility
     * @deprecated Use getClinicCohorts instead
     */
    static getGroupDevices(clinic_reference: string) {
        return ClinicService.getClinicCohorts(clinic_reference, true);
    }

    /**
     * Legacy method - maps to bulkAssociatePatients for device management compatibility
     * @deprecated Use patient association methods instead
     */
    static manageGroupDevices(clinic_reference: string, data: any, access_token: string) {
        // This would need to be adapted based on the specific data structure
        // For now, we'll map it to cohort management
        console.warn("manageGroupDevices is deprecated. Use patient association methods instead.");
        return axios
            .patch(
                CONSTANTS.HOST + "api/v1/cohort/clinics/" + clinic_reference + "/manage-cohorts/",
                data,
                {
                    headers: { 
                        authorization: "Bearer " + access_token,
                        "Content-Type": "application/json"
                    },
                }
            )
            .then((response: any) => response)
            .catch((err: any) => {
                console.log(err);
                return err.response;
            });
    }
}

export default ClinicService;